import type { BundleOption, BundleOptionSelection } from '@cobrastyle/shared-types';

/** Per-option selection: option_id → chosen selections (uid + quantity). */
export type BundleSelectionState = Record<number, Array<{ uid: string; quantity: number }>>;

const findSelection = (
  options: BundleOption[],
  uid: string,
): BundleOptionSelection | undefined => {
  for (const option of options) {
    const match = option.selections.find((s) => s.uid === uid);
    if (match) return match;
  }
  return undefined;
};

/** Live total: sum of each selected child's price × chosen quantity. */
export function computeBundleTotal(options: BundleOption[], state: BundleSelectionState): number {
  let total = 0;
  for (const chosen of Object.values(state)) {
    for (const { uid, quantity } of chosen) {
      const selection = findSelection(options, uid);
      if (!selection) continue;
      const unit = selection.productPrice ?? selection.price;
      total += unit * quantity;
    }
  }
  return total;
}

/** True when every required option group has at least one selection. */
export function validateBundleSelections(options: BundleOption[], state: BundleSelectionState): boolean {
  return options.every((option) => !option.required || (state[option.optionId]?.length ?? 0) > 0);
}

/** Rewrite the quantity segment of a Magento bundle uid ("bundle/opt/sel/qty"). */
export function reencodeUidQuantity(uid: string, quantity: number): string {
  try {
    const parts = atob(uid).split('/');
    if (parts.length === 4 && parts[0] === 'bundle') {
      parts[3] = String(quantity);
      return btoa(parts.join('/'));
    }
  } catch {
    // Not a decodable bundle uid — fall through.
  }
  return uid;
}

/** Flat list of selected_options uids, re-encoding quantities where changed. */
export function buildSelectedOptionUids(state: BundleSelectionState): string[] {
  const uids: string[] = [];
  for (const chosen of Object.values(state)) {
    for (const { uid, quantity } of chosen) {
      uids.push(reencodeUidQuantity(uid, quantity));
    }
  }
  return uids;
}

/** Initial state: preselect any is_default selections. */
export function defaultBundleState(options: BundleOption[]): BundleSelectionState {
  const state: BundleSelectionState = {};
  for (const option of options) {
    const defaults = option.selections.filter((s) => s.isDefault);
    if (defaults.length > 0) {
      state[option.optionId] = defaults.map((s) => ({ uid: s.uid, quantity: s.quantity }));
    }
  }
  return state;
}
